import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Table,
  Card,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Container,
  Pagination,
  Button,
  Modal,
  Row,
  Col,
  Badge
} from 'react-bootstrap';
import {
  Autocomplete,
  TextField,
  Checkbox,
  Box,
  Typography,
  Chip
} from '@mui/material';
import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { UserIcon, SearchIcon, ClearIcon, PlusIcon, EditIcon, DeleteIcon, AssignmentIcon } from '../components/icons/SettingsIcons';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { authAPI, clearCache, classroomAPI, childAPI } from '../utils/api';
import type { User, Role, CreateUserRequest, ClassroomAssignment, ChildAssignment } from '../types/auth';
import type { Classroom } from '../types/classroom';
import type { Child } from '../types/child';
import { formatDateTimeInNurseryTimezone } from '../utils/timezone';
import { validatePassword } from '../utils/passwordValidation';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const UserManagement: React.FC = () => {
  const { currentNursery, userRolePermissionsLoading } = useAuth();
  const {
    isSuperAdmin,
    canCreateUsers,
    canEditUsers,
    canDeleteUsers,
    canAssignUsers,
    canAccessUserManagement,
    canViewUsers
  } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'userManagement' });
  const { t: tc } = useTranslation('common');

  const isCurrentUserSuperAdmin = isSuperAdmin;
  const nurseryTimezone = currentNursery?.timezone || 'UTC';

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentUser, setAssignmentUser] = useState<User | null>(null);
  const [classroomAssignments, setClassroomAssignments] = useState<ClassroomAssignment[]>([]);
  const [childAssignments, setChildAssignments] = useState<ChildAssignment[]>([]);
  const [allClassrooms, setAllClassrooms] = useState<Classroom[]>([]);
  const [allChildren, setAllChildren] = useState<Child[]>([]);
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<number[]>([]);
  const [selectedChildIds, setSelectedChildIds] = useState<number[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentModalError, setAssignmentModalError] = useState<string | null>(null);
  const [assignmentModalSuccess, setAssignmentModalSuccess] = useState<string | null>(null);

  const sortedClassrooms = useMemo(() => {
    const assignedIds = new Set(classroomAssignments.map(a => a.classroom_id));
    return allClassrooms
      .filter(d => !assignedIds.has(d.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allClassrooms, classroomAssignments]);

  const sortedChildren = useMemo(() => {
    const assignedIds = new Set(childAssignments.map(a => a.child_id));
    return allChildren
      .filter(e => !assignedIds.has(e.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allChildren, childAssignments]);

  const [formData, setFormData] = useState<Omit<CreateUserRequest, 'nursery_id'>>({
    name: '',
    email: '',
    password: '',
    status: 1,
    superAdminFlag: 0,
    role_id: undefined
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState<Omit<CreateUserRequest, 'nursery_id'>>({
    name: '',
    email: '',
    password: '',
    status: 1,
    superAdminFlag: 0,
    role_id: undefined
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const [currentNurseryRoles, setCurrentNurseryRoles] = useState<Role[]>([]);
  const [currentNurseryRolesLoading, setCurrentNurseryRolesLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!currentNursery) return;

    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.getUsers(currentNursery.nursery_id);
      if (response.success) {
        setUsers(response.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError(error.response?.data?.message || error.message || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery, t]);

  useEffect(() => {
    if (canViewUsers) {
      fetchUsers();
    }
  }, [fetchUsers, canViewUsers]);

  useEffect(() => {
    const loadCurrentNurseryRoles = async () => {
      if (currentNursery && canAssignUsers) {
        try {
          setCurrentNurseryRolesLoading(true);
          const response = await authAPI.getRoles(currentNursery.nursery_id);
          if (response.success) {
            setCurrentNurseryRoles(response.data);
          }
        } catch (error) {
          console.error('Error loading current nursery roles:', error);
        } finally {
          setCurrentNurseryRolesLoading(false);
        }
      } else {
        setCurrentNurseryRoles([]);
      }
    };

    loadCurrentNurseryRoles();
  }, [currentNursery, canAssignUsers]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const searchLower = searchTerm.toLowerCase();
    return users.filter(user =>
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  }, [users, searchTerm]);

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      password: '',
      status: 1,
      superAdminFlag: 0,
      role_id: undefined
    });
  }, []);

  const handleShowModal = useCallback(() => {
    resetForm();
    setIsEditing(false);
    setEditingUser(null);
    setModalError(null);
    setModalSuccess(null);
    setShowModal(true);
  }, [resetForm]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setIsEditing(false);
    setEditingUser(null);
    setModalError(null);
    setModalSuccess(null);
    setError(null);
  }, []);

  const handleEditClick = useCallback((user: User) => {
    setEditingUser(user);
    setIsEditing(true);
    setEditFormData({
      name: user.name,
      email: user.email,
      password: '',
      status: user.status,
      superAdminFlag: user.superAdminFlag || 0,
      role_id: user.role_id || undefined
    });
    setModalError(null);
    setModalSuccess(null);
    setError(null);
    setShowModal(true);
  }, []);

  const handleDeleteClick = useCallback((user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
    setError(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setError(null);
  }, []);

  const fetchAssignments = useCallback(async (userId: number) => {
    if (!currentNursery) return;

    try {
      setAssignmentLoading(true);
      const [classroomRes, childRes] = await Promise.all([
        authAPI.getClassroomAssignments(currentNursery.nursery_id, userId),
        authAPI.getChildAssignments(currentNursery.nursery_id, userId)
      ]);

      if (classroomRes.success) setClassroomAssignments(classroomRes.data);
      if (childRes.success) setChildAssignments(childRes.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setAssignmentLoading(false);
    }
  }, [currentNursery]);

  useEffect(() => {
    if (assignmentModalSuccess) {
      const timer = setTimeout(() => setAssignmentModalSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [assignmentModalSuccess]);

  const handleManageAssignments = useCallback(async (user: User) => {
    if (!currentNursery) return;
    setAssignmentUser(user);
    setShowAssignmentModal(true);
    setAssignmentModalError(null);
    setAssignmentModalSuccess(null);
    setSelectedClassroomIds([]);
    setSelectedChildIds([]);

    fetchAssignments(user.id);

    try {
      const [classroomRes, childRes] = await Promise.all([
        classroomAPI.getClassrooms(currentNursery.nursery_id),
        childAPI.getChildren(currentNursery.nursery_id)
      ]);
      if (classroomRes && classroomRes.classrooms) setAllClassrooms(classroomRes.classrooms);
      if (childRes && childRes.success) setAllChildren(childRes.children);
    } catch (error) {
      console.error('Error fetching classrooms/children:', error);
    }
  }, [currentNursery, fetchAssignments]);

  const handleCloseAssignmentModal = useCallback(() => {
    setShowAssignmentModal(false);
    setAssignmentUser(null);
    setAssignmentModalError(null);
    setAssignmentModalSuccess(null);
    setSelectedClassroomIds([]);
    setSelectedChildIds([]);
    setClassroomAssignments([]);
    setChildAssignments([]);
  }, []);

  const handleAssignClassrooms = async () => {
    if (!currentNursery || !assignmentUser || selectedClassroomIds.length === 0) return;

    try {
      setAssignmentLoading(true);
      setAssignmentModalError(null);
      const response = await authAPI.assignClassrooms(currentNursery.nursery_id, {
        user_id: assignmentUser.id,
        classroom_ids: selectedClassroomIds
      });
      if (response.success) {
        setAssignmentModalSuccess(t('assignSuccess'));
        setSelectedClassroomIds([]);
        fetchAssignments(assignmentUser.id);
      }
    } catch (error: any) {
      setAssignmentModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleRemoveClassroomAssignment = async (assignmentId: number) => {
    if (!currentNursery || !assignmentUser) return;

    try {
      setAssignmentLoading(true);
      setAssignmentModalError(null);
      const response = await authAPI.removeClassroomAssignment(currentNursery.nursery_id, assignmentId);
      if (response.success) {
        setAssignmentModalSuccess(t('removeSuccess'));
        fetchAssignments(assignmentUser.id);
      }
    } catch (error: any) {
      setAssignmentModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAssignChildren = async () => {
    if (!currentNursery || !assignmentUser || selectedChildIds.length === 0) return;

    try {
      setAssignmentLoading(true);
      setAssignmentModalError(null);
      const response = await authAPI.assignChildren(currentNursery.nursery_id, {
        user_id: assignmentUser.id,
        child_ids: selectedChildIds
      });
      if (response.success) {
        setAssignmentModalSuccess(t('assignSuccess'));
        setSelectedChildIds([]);
        fetchAssignments(assignmentUser.id);
      }
    } catch (error: any) {
      setAssignmentModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleRemoveChildAssignment = async (assignmentId: number) => {
    if (!currentNursery || !assignmentUser) return;

    try {
      setAssignmentLoading(true);
      setAssignmentModalError(null);
      const response = await authAPI.removeChildAssignment(currentNursery.nursery_id, assignmentId);
      if (response.success) {
        setAssignmentModalSuccess(t('removeSuccess'));
        fetchAssignments(assignmentUser.id);
      }
    } catch (error: any) {
      setAssignmentModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleFormChange = useCallback((field: keyof Omit<CreateUserRequest, 'nursery_id'>, value: string | number | undefined) => {
    if (isEditing) {
      setEditFormData(prev => {
        const newData = { ...prev, [field]: value };
        if (field === 'superAdminFlag' && value === 1 && !isCurrentUserSuperAdmin) {
          newData.superAdminFlag = 0;
        }
        if (field === 'superAdminFlag' && value === 1) {
          newData.role_id = undefined;
        }
        return newData;
      });
    } else {
      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        if (field === 'superAdminFlag' && value === 1 && !isCurrentUserSuperAdmin) {
          newData.superAdminFlag = 0;
        }
        if (field === 'superAdminFlag' && value === 1) {
          newData.role_id = undefined;
        }
        return newData;
      });
    }

    if (modalError) setModalError(null);
    if (modalSuccess) setModalSuccess(null);
  }, [modalError, modalSuccess, isCurrentUserSuperAdmin, isEditing]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const currentFormData = isEditing ? editFormData : formData;

    if (!currentFormData.name.trim() || !currentFormData.email.trim() || (!isEditing && !currentFormData.password.trim())) {
      setModalError(t('fillRequired'));
      return;
    }

    if (!currentNursery) {
      setModalError(t('selectNurseryError'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(currentFormData.email)) {
      setModalError(t('validEmailError'));
      return;
    }

    if (currentFormData.password) {
      const validation = validatePassword(currentFormData.password);
      if (!validation.valid) {
        setModalError(validation.error || t('invalidPassword'));
        return;
      }
    }

    if (currentFormData.superAdminFlag === 1 && !isCurrentUserSuperAdmin) {
      setModalError(t('noSuperAdminPermission'));
      return;
    }

    try {
      setModalLoading(true);
      setModalError(null);

      if (isEditing && editingUser) {
        const payload: Partial<CreateUserRequest> = {
          name: editFormData.name,
          email: editFormData.email,
          status: editFormData.status,
          superAdminFlag: editFormData.superAdminFlag,
          role_id: editFormData.role_id
        };

        if (editFormData.password.trim()) {
          payload.password = editFormData.password;
        }

        const response = await authAPI.updateUser(currentNursery.nursery_id, editingUser.id, payload);
        if (response.success) {
          setModalSuccess(response.message || t('updateSuccess'));
          clearCache(`usersByNursery_${currentNursery.nursery_id}`);
          fetchUsers();
          setTimeout(() => handleCloseModal(), 2000);
        } else {
          setModalError(response.message || t('saveFailed'));
        }
      } else {
        const requestData: CreateUserRequest = {
          ...formData,
          nursery_id: currentNursery.nursery_id
        };

        if (!canAssignUsers || !formData.role_id) {
          delete requestData.role_id;
        }

        if (!isCurrentUserSuperAdmin) {
          requestData.superAdminFlag = 0;
        }

        const response = await authAPI.createUser(requestData);

        setModalSuccess(response.message || t('createSuccess'));
        clearCache(`usersByNursery_${currentNursery.nursery_id}`);
        fetchUsers();
        setTimeout(() => handleCloseModal(), 2000);
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      setModalError(error.response?.data?.message || error.message || t('saveFailed'));
    } finally {
      setModalLoading(false);
    }
  }, [formData, editFormData, isEditing, editingUser, isCurrentUserSuperAdmin, currentNursery, canAssignUsers, fetchUsers, handleCloseModal, t]);

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete || !currentNursery) return;

    try {
      setModalLoading(true);
      const response = await authAPI.deleteUser(currentNursery.nursery_id, userToDelete.id);
      if (response.success) {
        clearCache(`usersByNursery_${currentNursery.nursery_id}`);
        fetchUsers();
        handleCloseDeleteModal();
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      if (error.response?.status === 409) {
        setError(error.response?.data?.message || t('cannotDeleteUser'));
      } else {
        setError(error.response?.data?.message || error.message || t('deleteFailed'));
      }
      setShowDeleteModal(false);
      setUserToDelete(null);
    } finally {
      setModalLoading(false);
    }
  }, [userToDelete, currentNursery, fetchUsers, handleCloseDeleteModal, t]);

  if (userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
        </Container>
      </Layout>
    );
  }

  if (!canAccessUserManagement) {
    return (
      <Layout title={t('title')}>
        <Container className="py-5">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  const settingsPath = currentNursery ? `/nursery/${currentNursery.nursery_id}/settings` : '/settings';

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3 py-4">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={settingsPath}
          action={canCreateUsers && currentNursery ? (
            <Button variant="primary" onClick={handleShowModal}>
              <PlusIcon /> {t('addUser')}
            </Button>
          ) : undefined}
        />

        {!currentNursery && (
          <Alert variant="warning" role="alert">{t('selectNurseryWarning')}</Alert>
        )}

        {currentNursery && (
          <>
            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <InputGroup>
                  <InputGroup.Text id="user-search-label"><SearchIcon /></InputGroup.Text>
                  <Form.Control
                    placeholder={t('searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                    aria-label={t('searchPlaceholder')}
                    aria-describedby="user-search-label"
                  />
                  {searchTerm && (
                    <Button variant="outline-secondary" onClick={() => setSearchTerm('')} aria-label={tc('buttons.clear')}>
                      <ClearIcon />
                    </Button>
                  )}
                </InputGroup>
              </Card.Body>
            </Card>

            {error && <Alert variant="danger" role="alert">{error}</Alert>}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">{t('loading')}</p>
              </div>
            ) : (
              <Card className="shadow-sm border-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="px-4">{t('nameColumn')}</th>
                        <th>{t('userEmail')}</th>
                        <th>{t('role')}</th>
                        <th>{tc('labels.status')}</th>
                        <th>{t('createdAt')}</th>
                        <th>{t('updatedAt')}</th>
                        {(canEditUsers || canDeleteUsers || canAssignUsers) && <th className="text-end px-4">{tc('labels.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.length > 0 ? (
                        paginatedUsers.map((user) => (
                          <tr key={user.id}>
                            <td className="px-4 py-3">
                              <div className="d-flex align-items-center">
                                <UserIcon />
                                <span className="fw-medium">{user.name}</span>
                                {user.superAdminFlag === 1 && (
                                  <Badge bg="warning" text="dark" className="ms-2">{t('superAdminBadge')}</Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3">
                              <small className="text-muted">{user.email}</small>
                            </td>
                            <td className="py-3">
                              <small>{user.role_name || '—'}</small>
                            </td>
                            <td className="py-3">
                              <Badge bg={user.status === 1 ? 'success' : 'secondary'}>
                                {user.status === 1 ? tc('status.active') : tc('status.inactive')}
                              </Badge>
                            </td>
                            <td><small className="text-muted">{formatDateTimeInNurseryTimezone(user.created_at, nurseryTimezone)}</small></td>
                            <td><small className="text-muted">{formatDateTimeInNurseryTimezone(user.updated_at, nurseryTimezone)}</small></td>
                            {(canEditUsers || canDeleteUsers || canAssignUsers) && (
                              <td className="text-end px-4 py-3">
                                <div className="d-flex justify-content-end gap-2">
                                  {canAssignUsers && user.superAdminFlag !== 1 && (
                                    <Button
                                      variant="link"
                                      className="p-0 text-info"
                                      onClick={() => handleManageAssignments(user)}
                                      title={t('manageAssignments')}
                                    >
                                      <AssignmentIcon />
                                    </Button>
                                  )}
                                  {canEditUsers && (
                                    <Button
                                      variant="link"
                                      className="p-0 text-primary"
                                      onClick={() => handleEditClick(user)}
                                      title={t('editTitle')}
                                    >
                                      <EditIcon />
                                    </Button>
                                  )}
                                  {canDeleteUsers && (
                                    <Button
                                      variant="link"
                                      className="p-0 text-danger"
                                      onClick={() => handleDeleteClick(user)}
                                      title={t('deleteTitle')}
                                    >
                                      <DeleteIcon />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={(canEditUsers || canDeleteUsers || canAssignUsers) ? 7 : 6} className="text-center py-5 text-muted">
                            {searchTerm ? t('noMatchSearch') : t('noUsersForNursery')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                {totalPages > 1 && (
                  <Card.Footer className="bg-white px-4 py-3">
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 overflow-auto">
                      <small className="text-muted text-nowrap">
                        {tc('messages.showing', { from: (page - 1) * rowsPerPage + 1, to: Math.min(page * rowsPerPage, filteredUsers.length), total: filteredUsers.length, entity: '' })}
                      </small>
                      <Pagination className="mb-0 flex-wrap justify-content-center" aria-label={t('paginationLabel')}>
                        <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => p - 1)} />
                        {[...Array(totalPages)].map((_, i) => (
                          <Pagination.Item key={i + 1} active={i + 1 === page} onClick={() => setPage(i + 1)}>
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
                      </Pagination>
                    </div>
                  </Card.Footer>
                )}
              </Card>
            )}
          </>
        )}

        <Modal show={showModal} onHide={() => !modalLoading && handleCloseModal()} size="lg">
          <Modal.Header closeButton={!modalLoading}>
            <Modal.Title>{isEditing ? t('editTitle') : t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit} autoComplete="off">
            <Modal.Body className="px-4">
              {modalError && <Alert variant="danger" role="alert">{modalError}</Alert>}
              {modalSuccess && <Alert variant="success" role="status">{modalSuccess}</Alert>}

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('fullNameLabel')} <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('namePlaceholder')}
                      value={isEditing ? editFormData.name : formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      required
                      disabled={modalLoading}
                      autoComplete="new-password"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('emailAddressLabel')} <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={isEditing ? editFormData.email : formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      required
                      disabled={modalLoading}
                      autoComplete="new-password"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold text-dark">{t('nurseryLabel')}</Form.Label>
                <Form.Control
                  type="text"
                  value={currentNursery ? `${currentNursery.name} (${currentNursery.nursery_id})` : ''}
                  disabled
                />
              </Form.Group>

              <Row className="mb-3">
                <Col md={isCurrentUserSuperAdmin && canAssignUsers ? 4 : 6}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{tc('labels.status')}</Form.Label>
                    <Form.Select
                      value={isEditing ? editFormData.status : formData.status}
                      onChange={(e) => handleFormChange('status', Number(e.target.value))}
                      disabled={modalLoading}
                    >
                      <option value={1}>{tc('status.active')}</option>
                      <option value={0}>{tc('status.inactive')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {isCurrentUserSuperAdmin && (
                  <Col md={canAssignUsers ? 4 : 6}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark">{t('adminLevel')}</Form.Label>
                      <Form.Select
                        value={isEditing ? editFormData.superAdminFlag : formData.superAdminFlag}
                        onChange={(e) => handleFormChange('superAdminFlag', Number(e.target.value))}
                        disabled={modalLoading}
                      >
                        <option value={0}>{t('regularUser')}</option>
                        <option value={1}>{t('superAdminBadge')}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
                {canAssignUsers && (
                  <Col md={isCurrentUserSuperAdmin ? 4 : 6}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark">{t('roleOptional')}</Form.Label>
                      <Form.Select
                        value={isEditing ? (editFormData.role_id || '') : (formData.role_id || '')}
                        onChange={(e) => handleFormChange('role_id', e.target.value ? Number(e.target.value) : undefined)}
                        disabled={modalLoading || currentNurseryRolesLoading || (isEditing ? editFormData.superAdminFlag === 1 : formData.superAdminFlag === 1)}
                      >
                        <option value="">{t('noRole')}</option>
                        {currentNurseryRoles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.role_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">
                      {t('passwordLabel')} {!isEditing && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      placeholder={isEditing ? t('passwordPlaceholderEdit') : t('passwordPlaceholderCreate')}
                      value={isEditing ? editFormData.password : formData.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      required={!isEditing}
                      disabled={modalLoading}
                      autoComplete="new-password"
                    />
                    <Form.Text className="text-muted">
                      {t('passwordHint')}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="px-4">
              {!isEditing && (
                <Button variant="outline-secondary" onClick={() => { resetForm(); setModalError(null); setModalSuccess(null); }} disabled={modalLoading}>
                  {t('reset')}
                </Button>
              )}
              <Button variant="outline-secondary" onClick={() => handleCloseModal()} disabled={modalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : (isEditing ? t('saveChanges') : t('createUserBtn'))}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Assignment Modal */}
        <Modal show={showAssignmentModal} onHide={() => !assignmentLoading && handleCloseAssignmentModal()} size="lg">
          <Modal.Header closeButton={!assignmentLoading}>
            <Modal.Title>{t('assignmentsModalTitle')}: {assignmentUser?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4">
            {assignmentModalError && <Alert variant="danger" role="alert">{assignmentModalError}</Alert>}
            {assignmentModalSuccess && <Alert variant="success" role="status">{assignmentModalSuccess}</Alert>}

            <Row>
              <Col md={6} className="border-end">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <AssignmentIcon /> {t('classroomAssignments')}
                </h6>
                <div className="mb-4">
                  <Autocomplete
                    multiple
                    size="small"
                    options={sortedClassrooms}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={allClassrooms.filter(classroom => selectedClassroomIds.includes(classroom.id))}
                    onChange={(_, newValue) => {
                      setSelectedClassroomIds(newValue.map(v => v.id));
                    }}
                    slotProps={{
                      popper: {
                        sx: { zIndex: 1350 }
                      },
                      paper: {
                        sx: { zIndex: 1350 }
                      }
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.id}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('selectClassrooms')}
                        variant="outlined"
                        placeholder={t('classroomAssignments')}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={option.id}
                            label={option.name}
                            size="small"
                            {...tagProps}
                          />
                        );
                      })
                    }
                    disabled={assignmentLoading}
                  />
                  <Button
                    variant="primary"
                    className="mt-2 w-100"
                    onClick={handleAssignClassrooms}
                    disabled={assignmentLoading || selectedClassroomIds.length === 0}
                  >
                    {t('assignBtn')}
                  </Button>
                </div>

                <Typography variant="subtitle2" gutterBottom className="text-muted fw-bold">
                  {t('currentAssignments')}
                </Typography>
                <div className="assignment-list overflow-auto p-2 border rounded bg-light" style={{ maxHeight: '250px', minHeight: '100px' }}>
                  {assignmentLoading && classroomAssignments.length === 0 ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                  ) : classroomAssignments.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {classroomAssignments.map(assignment => (
                        <Badge key={assignment.id} bg="info" className="d-flex align-items-center p-2 shadow-sm">
                          <span className="me-2">{assignment.classroom?.name || `${t('idLabel')}: ${assignment.classroom_id}`}</span>
                          <span
                            className="ms-1 cursor-pointer bg-white text-info rounded-circle d-inline-flex align-items-center justify-content-center"
                            style={{ cursor: 'pointer', width: '16px', height: '16px', fontSize: '12px' }}
                            onClick={() => handleRemoveClassroomAssignment(assignment.id)}
                            title={t('removeBtn')}
                          >
                            &times;
                          </span>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted small">{t('noClassroomAssignments')}</div>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <UserIcon /> {t('childAssignments')}
                </h6>
                <div className="mb-4">
                  <Autocomplete
                    multiple
                    size="small"
                    options={sortedChildren}
                    disableCloseOnSelect
                    getOptionLabel={(option) => `${option.name} (${option.child_id})`}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={allChildren.filter(child => selectedChildIds.includes(child.id))}
                    onChange={(_, newValue) => {
                      setSelectedChildIds(newValue.map(v => v.id));
                    }}
                    slotProps={{
                      popper: {
                        sx: { zIndex: 1350 }
                      },
                      paper: {
                        sx: { zIndex: 1350 }
                      }
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.id}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        <Box>
                          <Typography variant="body2" className="fw-bold">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.child_id}</Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('selectChildren')}
                        variant="outlined"
                        placeholder={t('childAssignments')}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={option.id}
                            label={option.name}
                            size="small"
                            {...tagProps}
                          />
                        );
                      })
                    }
                    disabled={assignmentLoading}
                  />
                  <Button
                    variant="primary"
                    className="mt-2 w-100"
                    onClick={handleAssignChildren}
                    disabled={assignmentLoading || selectedChildIds.length === 0}
                  >
                    {t('assignBtn')}
                  </Button>
                </div>

                <Typography variant="subtitle2" gutterBottom className="text-muted fw-bold">
                  {t('currentAssignments')}
                </Typography>
                <div className="assignment-list overflow-auto p-2 border rounded bg-light" style={{ maxHeight: '250px', minHeight: '100px' }}>
                  {assignmentLoading && childAssignments.length === 0 ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                  ) : childAssignments.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {childAssignments.map(assignment => (
                        <Badge key={assignment.id} bg="success" className="d-flex align-items-center p-2 shadow-sm">
                          <span className="me-2">{assignment.child?.name || `${t('idLabel')}: ${assignment.child_id}`}</span>
                          <span
                            className="ms-1 cursor-pointer bg-white text-success rounded-circle d-inline-flex align-items-center justify-content-center"
                            style={{ cursor: 'pointer', width: '16px', height: '16px', fontSize: '12px' }}
                            onClick={() => handleRemoveChildAssignment(assignment.id)}
                            title={t('removeBtn')}
                          >
                            &times;
                          </span>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted small">{t('noChildAssignments')}</div>
                  )}
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAssignmentModal} disabled={assignmentLoading}>
              {tc('buttons.close')}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showDeleteModal} onHide={() => !modalLoading && handleCloseDeleteModal()} centered>
          <Modal.Header closeButton={!modalLoading}>
            <Modal.Title>{t('confirmDeleteTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('confirmDeleteMessage', { name: userToDelete?.name })}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseDeleteModal} disabled={modalLoading}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={modalLoading}>
              {modalLoading ? <Spinner animation="border" size="sm" /> : t('deleteUserBtn')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default UserManagement;
